import { forwardRef, type ComponentRef } from "react";

import { Typography } from "../../atoms/typography";
import { cn } from "../../lib";
import { TooltipArrowPrimitive, TooltipContentPrimitive } from "../../primitives";
import {
  tooltipArrowClasses,
  tooltipContainerClasses,
  tooltipContentBase,
  tooltipSideOffset,
  tooltipTextColor,
} from "./lib";
import type { TooltipContentProps } from "./types";

/**
 * Tooltip content surface.
 *
 * Applies the variant background, shadow, padding and max-width, and draws
 * its text as `Typography variant="caption-md"` in the variant's colour. It
 * takes text only (J6); rich or interactive content goes in a Popover.
 */
export const TooltipContent = forwardRef<
  ComponentRef<typeof TooltipContentPrimitive>,
  TooltipContentProps
>(
  (
    {
      variant = "default",
      side = "top",
      align = "center",
      sideOffset = tooltipSideOffset,
      className,
      children,
      testID,
      accessibilityLabel,
    },
    ref,
  ) => {
    return (
      <TooltipContentPrimitive
        ref={ref}
        side={side}
        align={align}
        sideOffset={sideOffset}
        testID={testID}
        accessibilityLabel={accessibilityLabel}
        className={cn(tooltipContentBase, tooltipContainerClasses[variant], className)}
      >
        <Typography variant="caption-md" color={tooltipTextColor[variant]}>
          {children}
        </Typography>
        <TooltipArrowPrimitive side={side} align={align} className={tooltipArrowClasses[variant]} />
      </TooltipContentPrimitive>
    );
  },
);

TooltipContent.displayName = "TooltipContent";
